// Eval execution boundary (10 DC-02 "eval runner"). Validation never trusts an author's
// `lastResult`: an eval counts only through an `EvalAttestation`, which `runEvals` alone mints
// (recorded in a module-private WeakSet and frozen) from an injected `EvalRunner`. Each
// attestation is bound to the template id, version id, the version's content hash, and the
// hash of the eval definition it ran. The real runners (fixture, replay, live) are future work;
// tests use a fake runner.
import { z } from "zod";
import { canonicalJson, sha256Hex } from "../canonical";
import { fromZod, type Result, untrustedInputErrors } from "../errors";
import {
  type TemplateEval,
  type TemplateVersion,
  templateEvalSchema,
  templateVersionSchema,
} from "../schemas";
import { templateContentHash } from "./content-hash";

export type EvalRunResult = { result: "pass" | "fail" | "error"; evidenceRef?: string };

export interface EvalRunner {
  /** Recorded on every attestation. */
  readonly name: string;
  run(input: {
    eval: TemplateEval;
    version: TemplateVersion;
    contentHash: string;
  }): Promise<EvalRunResult>;
}

export type EvalOutcome = "pass" | "fail" | "error" | "not_run";

export type EvalAttestation = Readonly<{
  templateId: string;
  versionId: string;
  contentHash: string;
  evalKey: string;
  evalHash: string;
  kind: TemplateEval["kind"];
  result: EvalOutcome;
  runner: string;
  evidenceRef: string | null;
}>;

const minted = new WeakSet<object>();

/** True only for attestations minted by {@link runEvals}. */
export function isEvalAttestation(a: unknown): a is EvalAttestation {
  return typeof a === "object" && a !== null && minted.has(a);
}

/** sha256 over the canonical eval definition (ignored author fields excluded). */
export function evalHash(e: TemplateEval): string {
  return sha256Hex(canonicalJson({ key: e.key, kind: e.kind, specRef: e.specRef }));
}

const runResultSchema = z.strictObject({
  result: z.enum(["pass", "fail", "error"]),
  evidenceRef: z.string().min(1).optional(),
});

export type RunEvalsOptions = {
  /** Evals to run against this version; defaults to the version's own. */
  evals?: unknown[];
  /** `live` evals run only when true (DC-02: "`live` behind a flag"); otherwise `not_run`. */
  allowLive?: boolean;
};

export async function runEvals(
  runner: EvalRunner,
  versionInput: unknown,
  options: RunEvalsOptions = {},
): Promise<Result<EvalAttestation[]>> {
  const unsafe = untrustedInputErrors(versionInput);
  if (unsafe.length > 0) return { ok: false, errors: unsafe };
  const parsed = templateVersionSchema.safeParse(versionInput);
  if (!parsed.success) return { ok: false, errors: fromZod(parsed.error) };
  const version = parsed.data;
  let evals: TemplateEval[] = version.evals;
  if (options.evals !== undefined) {
    const unsafeEvals = untrustedInputErrors(options.evals, "evals");
    if (unsafeEvals.length > 0) return { ok: false, errors: unsafeEvals };
    const e = z.array(templateEvalSchema).safeParse(options.evals);
    if (!e.success) return { ok: false, errors: fromZod(e.error, "schema_invalid", "evals") };
    evals = e.data;
  }
  const contentHash = templateContentHash(version);

  const out: EvalAttestation[] = [];
  for (const e of evals) {
    let result: EvalOutcome = "not_run";
    let evidenceRef: string | null = null;
    if (e.kind !== "live" || options.allowLive === true) {
      try {
        const raw = runResultSchema.safeParse(await runner.run({ eval: e, version, contentHash }));
        if (raw.success) {
          result = raw.data.result;
          evidenceRef = raw.data.evidenceRef ?? null;
        } else result = "error";
      } catch {
        result = "error";
      }
    }
    const a: EvalAttestation = Object.freeze({
      templateId: version.templateId,
      versionId: version.versionId,
      contentHash,
      evalKey: e.key,
      evalHash: evalHash(e),
      kind: e.kind,
      result,
      runner: runner.name,
      evidenceRef,
    });
    minted.add(a);
    out.push(a);
  }
  return { ok: true, value: out };
}
